import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { authService } from './services/authService';
import { RegisterData, User, VerifyCodeData } from './types';
import { AuthContext, AuthContextType } from './contexts/AuthContext';

// Toast system
import RootMeetingDrawer from './components/meetings/RootMeetingDrawer';
import ToastContainer from './components/Toast';
import { MeetingDrawerProvider } from './contexts/MeetingDrawerContext';
import { ToastProvider } from './contexts/ToastContext';

// New Navigation System
import { OrgRouteLayout, ProjectRouteLayout, SettingsLayout } from './components/navigation';
import ProfileLayout from './components/navigation/ProfileLayout';

// Components
import LoadingSpinner from './components/LoadingSpinner';
import PageTransition from './components/PageTransition';

// Auth Pages
import ForgotPassword from './pages/ForgotPassword';
import InvitationAcceptance from './pages/InvitationAcceptance';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import ResetPasswordOTP from './pages/ResetPasswordOTP';
import VerificationCode from './pages/VerificationCode';

// Organization Pages
import { DashboardsNew as Dashboard } from './pages/DashboardsNew';
import AllProjects from './pages/AllProjects';
import CreateProject from './pages/CreateProject';
import Notifications from './pages/Notifications';
import GlobalTasks from './pages/GlobalTasks';
import Vendors from './pages/Vendors';

// Project Module Pages
import BidRequests from './pages/BidRequests';
import Bills from './pages/Bills';
import Budgets from './pages/Budgets';
import Calendar from './pages/Calendar';
import ChangeOrders from './pages/ChangeOrders';
import ClientChangeOrderPortal from './pages/ClientChangeOrderPortal';
import ClientInvoices from './pages/ClientInvoices';
import ComplianceTracking from './pages/ComplianceTracking';
import Contracts from './pages/Contracts';
import DailyLogs from './pages/DailyLogs';
import Deliveries from './pages/Deliveries';
import Drawings from './pages/Drawings';
import Equipment from './pages/Equipment';
import FieldReports from './pages/FieldReports';
import Files from './pages/Files';
import IncidentReporting from './pages/IncidentReporting';
import Invoicing from './pages/Invoicing';
import Manpower from './pages/Manpower';
import Meetings from './pages/Meetings';
import Milestones from './pages/Milestones';
import PhotosRedux from './pages/PhotosRedux';
import ProjectDashboard from './pages/ProjectDashboard';
import PunchList from './pages/PunchList';
import PurchaseOrders from './pages/PurchaseOrders';
import QualityReports from './pages/QualityReports';
import RFIs from './pages/RFIs';
import SafetyReports from './pages/SafetyReports';
import Schedule from './pages/Schedule';
import Specifications from './pages/Specifications';
import Submittals from './pages/Submittals';
import Tasks from './pages/Tasks';
import Team from './pages/Team';
import Timesheets from './pages/Timesheets';
import Todos from './pages/Todos';
import Estimates from './pages/Estimates';
import Proposals from './pages/Proposals';
import Takeoff from './pages/Takeoff';

// Settings Pages
import DropdownAdmin from './pages/DropdownAdmin';
import EmailTemplates from './pages/EmailTemplates';
import NotificationTemplates from './pages/NotificationTemplates';
import Settings from './pages/Settings';
import PdfTemplates from './pages/PdfTemplates';
import SMSTemplates from './pages/SMSTemplates';
import SystemSettings from './pages/SystemSettings';

// Profile Pages
import ProfileOverview from './pages/profile/ProfileOverview';
import ProfileSecurity from './pages/profile/ProfileSecurity';
import ProfileOrganizations from './pages/profile/ProfileOrganizations';
import ProfileActivity from './pages/profile/ProfileActivity';
import ProfileNotifications from './pages/profile/ProfileNotifications';
import ProfilePreferences from './pages/profile/ProfilePreferences';

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  isAuthenticated: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, isAuthenticated }) => {
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Placeholder component for modules not yet implemented
const ComingSoon: React.FC<{ title: string }> = ({ title }) => (
  <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-4">
    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
        />
      </svg>
    </div>
    <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
    <p className="text-gray-500 max-w-md">
      This module is coming soon. We're working hard to bring you this feature.
    </p>
  </div>
);

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing authentication on app load
  useEffect(() => {
    const initAuth = async () => {
      try {
        const isAuth = authService.isAuthenticated();
        if (isAuth) {
          const storedUser = authService.getStoredUser();
          if (storedUser) {
            setUser(storedUser);
          } else {
            // Try to fetch current user from API
            const response = await authService.getCurrentUser();
            if (response.success && response.data) {
              setUser(response.data);
            } else {
              // Clear invalid token
              await authService.logout();
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        await authService.logout();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (
    email: string,
    password: string
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await authService.login({ email, password });
      if (response.success && response.data) {
        setUser(response.data.user);
        return { success: true };
      }
      return { success: false, message: response.message || 'Login failed' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'An unexpected error occurred' };
    }
  };

  const register = async (
    userData: RegisterData
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await authService.register(userData);
      if (response.success) {
        return { success: true, message: response.message || 'Registration successful' };
      }
      return { success: false, message: response.message || 'Registration failed' };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, message: 'An unexpected error occurred' };
    }
  };

  const verifyCode = async (
    verifyData: VerifyCodeData
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await authService.verifyCode(verifyData);
      if (response.success && response.data) {
        setUser(response.data.user);
        return { success: true, message: response.message || 'Verification successful' };
      }
      return { success: false, message: response.message || 'Verification failed' };
    } catch (error) {
      console.error('Verification error:', error);
      return { success: false, message: 'An unexpected error occurred' };
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authService.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const updateUser = (userData: Partial<User>): void => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const authContextValue: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    verifyCode,
    logout,
    updateUser,
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <ToastProvider>
      <MeetingDrawerProvider>
        <AuthContext.Provider value={authContextValue}>
          <div className="App">
            <Routes>
              {/* Public routes */}
              <Route
                path="/"
                element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />}
              />
              <Route
                path="/login"
                element={user ? <Navigate to="/dashboard" replace /> : <Login />}
              />
              <Route
                path="/register"
                element={user ? <Navigate to="/dashboard" replace /> : <Register />}
              />
              <Route
                path="/verification"
                element={user ? <Navigate to="/dashboard" replace /> : <VerificationCode />}
              />
              <Route
                path="/forgot-password"
                element={user ? <Navigate to="/dashboard" replace /> : <ForgotPassword />}
              />
              <Route
                path="/reset-password-otp"
                element={user ? <Navigate to="/dashboard" replace /> : <ResetPasswordOTP />}
              />
              <Route
                path="/reset-password"
                element={user ? <Navigate to="/dashboard" replace /> : <ResetPassword />}
              />

              {/* Public invitation acceptance route */}
              <Route path="/invitation/accept/:token" element={<InvitationAcceptance />} />

              {/* Public Change Order Client Portal */}
              <Route
                path="/change-orders/client/:accessToken"
                element={<ClientChangeOrderPortal />}
              />

              {/* Organization-level routes (no project sidebar) */}
              <Route
                element={
                  <ProtectedRoute isAuthenticated={!!user}>
                    <OrgRouteLayout />
                  </ProtectedRoute>
                }
              >
                <Route
                  path="/dashboard"
                  element={
                    <PageTransition>
                      <Dashboard />
                    </PageTransition>
                  }
                />
                <Route
                  path="/projects"
                  element={
                    <PageTransition>
                      <AllProjects />
                    </PageTransition>
                  }
                />
                <Route
                  path="/projects/create"
                  element={
                    <PageTransition>
                      <CreateProject />
                    </PageTransition>
                  }
                />
                <Route
                  path="/projects/edit/:id"
                  element={
                    <PageTransition>
                      <CreateProject />
                    </PageTransition>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <PageTransition>
                      <Notifications />
                    </PageTransition>
                  }
                />
                <Route
                  path="/global/tasks"
                  element={
                    <PageTransition>
                      <GlobalTasks />
                    </PageTransition>
                  }
                />
                <Route
                  path="/global/contracts"
                  element={
                    <PageTransition>
                      <Contracts />
                    </PageTransition>
                  }
                />
                <Route
                  path="/vendors"
                  element={
                    <PageTransition>
                      <Vendors />
                    </PageTransition>
                  }
                />
                <Route
                  path="/dashboard/calendar"
                  element={
                    <PageTransition>
                      <Calendar />
                    </PageTransition>
                  }
                />
              </Route>

              {/* Project-level routes (with project sidebar) */}
              <Route
                path="/projects/:projectId"
                element={
                  <ProtectedRoute isAuthenticated={!!user}>
                    <ProjectRouteLayout />
                  </ProtectedRoute>
                }
              >
                {/* Overview */}
                <Route
                  path="dashboard"
                  element={
                    <PageTransition>
                      <ProjectDashboard />
                    </PageTransition>
                  }
                />

                {/* Planning & Pre-construction */}
                <Route
                  path="takeoff"
                  element={
                    <PageTransition>
                      <Takeoff />
                    </PageTransition>
                  }
                />
                <Route
                  path="estimates"
                  element={
                    <PageTransition>
                      <Estimates />
                    </PageTransition>
                  }
                />
                <Route
                  path="proposal"
                  element={
                    <PageTransition>
                      <Proposals />
                    </PageTransition>
                  }
                />
                <Route
                  path="schedule"
                  element={
                    <PageTransition>
                      <Schedule />
                    </PageTransition>
                  }
                />
                <Route
                  path="bid-requests"
                  element={
                    <PageTransition>
                      <BidRequests />
                    </PageTransition>
                  }
                />
                <Route
                  path="selections"
                  element={
                    <PageTransition>
                      <ComingSoon title="Selections and Allowances" />
                    </PageTransition>
                  }
                />

                {/* Financials */}
                <Route
                  path="purchase-orders"
                  element={
                    <PageTransition>
                      <PurchaseOrders />
                    </PageTransition>
                  }
                />
                <Route
                  path="bills"
                  element={
                    <PageTransition>
                      <Bills />
                    </PageTransition>
                  }
                />
                <Route
                  path="change-orders"
                  element={
                    <PageTransition>
                      <ChangeOrders />
                    </PageTransition>
                  }
                />
                <Route
                  path="budget"
                  element={
                    <PageTransition>
                      <Budgets />
                    </PageTransition>
                  }
                />
                <Route
                  path="client-invoices"
                  element={
                    <PageTransition>
                      <ClientInvoices />
                    </PageTransition>
                  }
                />
                <Route
                  path="invoicing"
                  element={
                    <PageTransition>
                      <Invoicing />
                    </PageTransition>
                  }
                />

                {/* Operations */}
                <Route
                  path="daily-logs"
                  element={
                    <PageTransition>
                      <DailyLogs />
                    </PageTransition>
                  }
                />
                <Route
                  path="timesheets"
                  element={
                    <PageTransition>
                      <Timesheets />
                    </PageTransition>
                  }
                />
                <Route
                  path="todos"
                  element={
                    <PageTransition>
                      <Todos />
                    </PageTransition>
                  }
                />
                <Route
                  path="tasks"
                  element={
                    <PageTransition>
                      <Tasks />
                    </PageTransition>
                  }
                />
                <Route
                  path="rfis"
                  element={
                    <PageTransition>
                      <RFIs />
                    </PageTransition>
                  }
                />
                <Route
                  path="milestones"
                  element={
                    <PageTransition>
                      <Milestones />
                    </PageTransition>
                  }
                />

                {/* Documents & Media */}
                <Route
                  path="files"
                  element={
                    <PageTransition>
                      <Files />
                    </PageTransition>
                  }
                />
                <Route
                  path="specifications"
                  element={
                    <PageTransition>
                      <Specifications />
                    </PageTransition>
                  }
                />
                <Route
                  path="submittals"
                  element={
                    <PageTransition>
                      <Submittals />
                    </PageTransition>
                  }
                />
                <Route
                  path="drawings"
                  element={
                    <PageTransition>
                      <Drawings />
                    </PageTransition>
                  }
                />
                <Route
                  path="photos"
                  element={
                    <PageTransition>
                      <PhotosRedux />
                    </PageTransition>
                  }
                />

                {/* Field & Compliance */}
                <Route
                  path="deliveries"
                  element={
                    <PageTransition>
                      <Deliveries />
                    </PageTransition>
                  }
                />
                <Route
                  path="equipment"
                  element={
                    <PageTransition>
                      <Equipment />
                    </PageTransition>
                  }
                />
                <Route
                  path="incidents"
                  element={
                    <PageTransition>
                      <IncidentReporting />
                    </PageTransition>
                  }
                />
                <Route
                  path="quality"
                  element={
                    <PageTransition>
                      <QualityReports />
                    </PageTransition>
                  }
                />
                <Route
                  path="safety"
                  element={
                    <PageTransition>
                      <SafetyReports />
                    </PageTransition>
                  }
                />
                <Route
                  path="field-reports"
                  element={
                    <PageTransition>
                      <FieldReports />
                    </PageTransition>
                  }
                />
                <Route
                  path="punch-list"
                  element={
                    <PageTransition>
                      <PunchList />
                    </PageTransition>
                  }
                />
                <Route
                  path="compliance"
                  element={
                    <PageTransition>
                      <ComplianceTracking />
                    </PageTransition>
                  }
                />

                {/* Team & Contracts */}
                <Route
                  path="team"
                  element={
                    <PageTransition>
                      <Team />
                    </PageTransition>
                  }
                />
                <Route
                  path="contracts"
                  element={
                    <PageTransition>
                      <Contracts />
                    </PageTransition>
                  }
                />
                <Route
                  path="contracts/:contractId"
                  element={
                    <PageTransition>
                      <Contracts />
                    </PageTransition>
                  }
                />
                <Route
                  path="manpower"
                  element={
                    <PageTransition>
                      <Manpower />
                    </PageTransition>
                  }
                />
                <Route
                  path="meetings"
                  element={
                    <PageTransition>
                      <Meetings />
                    </PageTransition>
                  }
                />
                <Route
                  path="calendar"
                  element={
                    <PageTransition>
                      <Calendar />
                    </PageTransition>
                  }
                />
              </Route>

              {/* Settings routes (with settings sidebar) */}
              <Route
                path="/settings"
                element={
                  <ProtectedRoute isAuthenticated={!!user}>
                    <SettingsLayout />
                  </ProtectedRoute>
                }
              >
                <Route
                  index
                  element={
                    <PageTransition>
                      <Settings />
                    </PageTransition>
                  }
                />
                <Route
                  path="system"
                  element={
                    <PageTransition>
                      <SystemSettings />
                    </PageTransition>
                  }
                />
                <Route
                  path="templates/email"
                  element={
                    <PageTransition>
                      <EmailTemplates />
                    </PageTransition>
                  }
                />
                <Route
                  path="templates/sms"
                  element={
                    <PageTransition>
                      <SMSTemplates />
                    </PageTransition>
                  }
                />
                <Route
                  path="templates/notifications"
                  element={
                    <PageTransition>
                      <NotificationTemplates />
                    </PageTransition>
                  }
                />
                <Route
                  path="templates/pdf"
                  element={
                    <PageTransition>
                      <PdfTemplates />
                    </PageTransition>
                  }
                />
                <Route
                  path="dropdowns"
                  element={
                    <PageTransition>
                      <DropdownAdmin />
                    </PageTransition>
                  }
                />
                <Route
                  path="admin/dropdowns"
                  element={
                    <PageTransition>
                      <DropdownAdmin />
                    </PageTransition>
                  }
                />
              </Route>

              {/* Profile routes (with profile sidebar) */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute isAuthenticated={!!user}>
                    <ProfileLayout />
                  </ProtectedRoute>
                }
              >
                <Route
                  index
                  element={
                    <PageTransition>
                      <ProfileOverview />
                    </PageTransition>
                  }
                />
                <Route
                  path="security"
                  element={
                    <PageTransition>
                      <ProfileSecurity />
                    </PageTransition>
                  }
                />
                <Route
                  path="organizations"
                  element={
                    <PageTransition>
                      <ProfileOrganizations />
                    </PageTransition>
                  }
                />
                <Route
                  path="activity"
                  element={
                    <PageTransition>
                      <ProfileActivity />
                    </PageTransition>
                  }
                />
                <Route
                  path="notifications"
                  element={
                    <PageTransition>
                      <ProfileNotifications />
                    </PageTransition>
                  }
                />
                <Route
                  path="preferences"
                  element={
                    <PageTransition>
                      <ProfilePreferences />
                    </PageTransition>
                  }
                />
              </Route>

              {/* Legacy route redirects for backward compatibility */}
              <Route path="/dashboard/projects" element={<Navigate to="/projects" replace />} />
              <Route
                path="/dashboard/projects/:id"
                element={<Navigate to="/projects/:id/dashboard" replace />}
              />
              <Route path="/dashboard/settings" element={<Navigate to="/settings" replace />} />
              <Route path="/dashboard/settings/*" element={<Navigate to="/settings" replace />} />
              <Route path="/dashboard/tasks" element={<Navigate to="/global/tasks" replace />} />
              <Route
                path="/dashboard/contracts"
                element={<Navigate to="/global/contracts" replace />}
              />
              <Route
                path="/dashboard/notifications"
                element={<Navigate to="/notifications" replace />}
              />

              {/* Redirect authenticated users to dashboard */}
              <Route
                path="/app/*"
                element={
                  <ProtectedRoute isAuthenticated={!!user}>
                    <Navigate to="/dashboard" replace />
                  </ProtectedRoute>
                }
              />
            </Routes>
            <ToastContainer />
            {/* Root-level Meeting Drawer */}
            <RootMeetingDrawer />
          </div>
        </AuthContext.Provider>
      </MeetingDrawerProvider>
    </ToastProvider>
  );
}

export default App;
